import { createBrowserRouter, type RouteObject, Outlet } from 'react-router-dom'
import Layout from './modules/layout/templates/Layout'
import HomePage from './modules/home/templates/HomePage'
import FAQ from './pages/FAQ'
import ProductDetailPage from './modules/products/templates/ProductDetailPage'
import CartPage from './modules/cart/templates/CartPage'
import QuickOrderPage from './modules/quick-order/templates/QuickOrderPage'
import AIDisclosurePage from './pages/AIDisclosurePage'
import CategoriesPage from './modules/products/templates/CategoriesPage'
import ProductsPage from './modules/products/templates/ProductsPage'
import CategoryDetailPage from './modules/products/templates/CategoryDetailPage.tsx'
import CheckoutPage from './modules/checkout/templates/CheckoutPage'
import LoginPage from './modules/auth/templates/LoginPage'
import RegisterPage from './modules/auth/templates/RegisterPage'
import AccountPage from './modules/auth/templates/AccountPage'
import OrdersPage from './modules/auth/templates/OrdersPage'
import OrderDetailPage from './modules/auth/templates/OrderDetailPage'
import PrivateRoute from './modules/auth/components/PrivateRoute'
import PasswordPage from './modules/auth/templates/PasswordPage'
import SetPasswordPage from './modules/auth/templates/SetPasswordPage'
import PreferencesPage from './modules/auth/templates/PreferencesPage'
import AddressesPage from './modules/auth/templates/AddressesPage'
import PaymentMethodsPage from './modules/auth/templates/PaymentMethodsPage'
import FavoritesPage from './modules/auth/templates/FavoritesPage'
import DropShippingEmailSettingsPage from './modules/auth/templates/DropShippingEmailSettingsPage'
import BlogPage from './modules/blog/templates/BlogPage'
import BlogPostDetail from './modules/blog/templates/BlogPostDetail'
import NotFoundPage from './modules/error/NotFoundPage'
import PreviewPage from './modules/preview/PreviewPage'
import EnviosPagos from './modules/home/pages/EnviosPagos'
import Brands from './pages/Brands'
import Nosotros from './pages/Nosotros'
import Contacto from './pages/legal/Contacto'
import Catalog from './pages/Catalog'
import TerminosCondiciones from './pages/legal/TerminosCondiciones'
import PoliticaPrivacidad from './pages/legal/PoliticaPrivacidad'
import PoliticaCookies from './pages/legal/PoliticaCookies'
import AvisoLegal from './pages/legal/AvisoLegal'
import EnviosDevoluciones from './pages/legal/EnviosDevoluciones'
// Importaciones para páginas de categorías específicas
import DogsProductsPage from './modules/categories/templates/DogsProductsPage'
import CatsProductsPage from './modules/categories/templates/CatsProductsPage'
import RodentsProductsPage from './modules/categories/templates/RodentsProductsPage'
import BirdsProductsPage from './modules/categories/templates/BirdsProductsPage'
// Importaciones para el panel de administración
import AdminRoute from './modules/admin/components/AdminRoute'
import AdminDashboard from './modules/admin/templates/AdminDashboard'
import UsersPage from './modules/admin/templates/UsersPage'
import AdminProductsPage from './modules/admin/templates/ProductsPage'
import AdminOrdersPage from './modules/admin/templates/OrdersPage'
import ProductFormPage from './modules/admin/templates/ProductFormPage'
import UserFormPage from './modules/admin/templates/UserFormPage'
import PendingUsersPage from './modules/admin/templates/PendingUsersPage'
import AdminOrderDetailPage from './modules/admin/templates/OrderDetailPage'
import CategoryFormPage from './modules/admin/templates/CategoryFormPage'
import AnalyticsPage from './modules/admin/templates/analytics/AnalyticsPage'
import SalesAnalyticsPage from './modules/admin/templates/analytics/SalesAnalyticsPage'
import ProductAnalyticsPage from './modules/admin/templates/analytics/ProductAnalyticsPage'
import CustomerAnalyticsPage from './modules/admin/templates/analytics/CustomerAnalyticsPage'
import FunnelAnalyticsPage from './modules/admin/templates/analytics/FunnelAnalyticsPage'
import AdminCategoriesPage from './modules/admin/templates/CategoriesPage'
import ReviewsPage from './modules/admin/templates/ReviewsPage'
import ChatFeedbackPage from './modules/admin/templates/ChatFeedbackPage'
import SettingsPage from './modules/admin/templates/SettingsPage'
import GlobalDiscountsPage from './modules/admin/templates/GlobalDiscountsPage'
import PopupsPage from './modules/admin/templates/PopupsPage'
import AdminStoreCategoriesPage from './modules/admin/templates/StoreCategoriesPage'
import StoresLocatorPage from './pages/StoresLocatorPage'
// Comercial
import CommercialRoute from './modules/commercial/components/CommercialRoute'
import CommercialLayout from './modules/commercial/templates/Layout'
import CommercialDashboard from './modules/commercial/templates/CommercialDashboard'
import CommercialCustomersPage from './modules/commercial/templates/CustomersPage'
import CommercialPrivateCustomersPage from './modules/commercial/templates/PrivateCustomersPage'
import CommercialOrdersPage from './modules/commercial/templates/OrdersPage'

// Verificación de si estamos en el navegador o en el servidor
const isBrowser = typeof window !== 'undefined';

// Configuración de rutas para compartir entre cliente y servidor
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'preview',
        element: <PreviewPage />
      },
      {
        path: 'faq',
        element: <FAQ />
      },
      {
        path: 'products',
        element: <ProductsPage />,
      },
      {
        path: 'products/:id',
        element: <ProductDetailPage />,
      },
      {
        path: 'cart',
        element: <CartPage />
      },
      {
        path: 'checkout',
        element: <PrivateRoute><CheckoutPage /></PrivateRoute>
      },
      {
        path: 'categories',
        element: <CategoriesPage />
      },
      {
        path: 'categories/:slug',
        element: <CategoryDetailPage />
      },
      {
        path: 'login',
        element: <LoginPage />
      },
      {
        path: 'register',
        element: <RegisterPage />
      },
      {
        path: 'set-password/:token',
        element: <SetPasswordPage />
      },
      {
        path: 'blog',
        element: <BlogPage />
      },
      {
        path: 'blog/:slug',
        element: <BlogPostDetail />,
      },
      {
        path: 'account',
        element: <PrivateRoute><AccountPage /></PrivateRoute>
      },
      {
        path: 'account/password',
        element: <PrivateRoute><PasswordPage /></PrivateRoute>
      },
      {
        path: 'account/preferences',
        element: <PrivateRoute><PreferencesPage /></PrivateRoute>
      },
      {
        path: 'account/addresses',
        element: <PrivateRoute><AddressesPage /></PrivateRoute>
      },
      {
        path: 'account/payment-methods',
        element: <PrivateRoute><PaymentMethodsPage /></PrivateRoute>
      },
      {
        path: 'account/drop-shipping-emails',
        element: <PrivateRoute><DropShippingEmailSettingsPage /></PrivateRoute>
      },
      {
        path: 'account/favorites',
        element: <PrivateRoute><FavoritesPage /></PrivateRoute>
      },
      {
        path: 'orders',
        element: <PrivateRoute><OrdersPage /></PrivateRoute>
      },
      {
        path: 'orders/:orderId',
        element: <PrivateRoute><OrderDetailPage /></PrivateRoute>,
      },
      {
        path: 'envios-y-pagos',
        element: <EnviosPagos />
      },
      {
        path: 'brands',
        element: <Brands />
      },
      {
        path: 'nosotros',
        element: <Nosotros />
      },
      {
        path: 'about-us',
        element: <Nosotros />
      },
      {
        path: 'contact',
        element: <Contacto />
      },
      {
        path: 'catalog',
        element: <Catalog />
      },
      {
        path: 'terminos-y-condiciones',
        element: <TerminosCondiciones />
      },
      {
        path: 'politica-de-privacidad',
        element: <PoliticaPrivacidad />
      },
      {
        path: 'politica-de-cookies',
        element: <PoliticaCookies />
      },
      {
        path: 'aviso-legal',
        element: <AvisoLegal />
      },
      {
        path: 'envios-y-devoluciones',
        element: <EnviosDevoluciones />
      },
      {
        path: 'preguntas-frecuentes',
        element: <FAQ />
      },
      {
        path: 'distribuidores',
        element: <StoresLocatorPage />
      },
      // Rutas de categorías específicas
      {
        path: 'dogs',
        element: <DogsProductsPage />
      },
      {
        path: 'cats',
        element: <CatsProductsPage />
      },
      {
        path: 'rodents',
        element: <RodentsProductsPage />
      },
      {
        path: 'birds',
        element: <BirdsProductsPage />
      },
      // Rutas del panel de administración
      {
        path: 'admin/dashboard',
        element: <AdminRoute><AdminDashboard /></AdminRoute>
      },
      {
        path: 'admin/users',
        element: <AdminRoute><UsersPage /></AdminRoute>
      },
      {
        path: 'admin/users/pending',
        element: <AdminRoute><PendingUsersPage /></AdminRoute>
      },
      {
        path: 'admin/users/new',
        element: <AdminRoute><UserFormPage /></AdminRoute>
      },
      {
        path: 'admin/users/:id/edit',
        element: <AdminRoute><UserFormPage /></AdminRoute>
      },
      {
        path: 'admin/products',
        element: <AdminRoute><AdminProductsPage /></AdminRoute>
      },
      {
        path: 'admin/products/new',
        element: <AdminRoute><ProductFormPage /></AdminRoute>
      },
      {
        path: 'admin/products/:id/edit',
        element: <AdminRoute><ProductFormPage /></AdminRoute>
      },
      {
        path: 'admin/orders',
        element: <AdminRoute><AdminOrdersPage /></AdminRoute>
      },
      {
        path: 'admin/orders/:id',
        element: <AdminRoute><AdminOrderDetailPage /></AdminRoute>
      },
      {
        path: 'admin/chat-feedback',
        element: <AdminRoute><ChatFeedbackPage /></AdminRoute>
      },
      {
        path: 'admin/settings',
        element: <AdminRoute><SettingsPage /></AdminRoute>
      },
      {
        path: 'admin/discounts',
        element: <AdminRoute><GlobalDiscountsPage /></AdminRoute>
      },
      {
        path: 'admin/popups',
        element: <AdminRoute><PopupsPage /></AdminRoute>
      },
      {
        path: 'admin/categories',
        element: <AdminRoute><AdminCategoriesPage /></AdminRoute>
      },
      {
        path: 'admin/categories/new',
        element: <AdminRoute><CategoryFormPage /></AdminRoute>
      },
      {
        path: 'admin/categories/:id/edit',
        element: <AdminRoute><CategoryFormPage /></AdminRoute>
      },
      {
        path: 'admin/store-categories',
        element: <AdminRoute><AdminStoreCategoriesPage /></AdminRoute>
      },
      {
        path: 'admin/reviews',
        element: <AdminRoute><ReviewsPage /></AdminRoute>
      },
      {
        path: 'admin/analytics',
        element: <AdminRoute><AnalyticsPage /></AdminRoute>
      },
      {
        path: 'admin/analytics/sales',
        element: <AdminRoute><SalesAnalyticsPage /></AdminRoute>
      },
      {
        path: 'admin/analytics/products',
        element: <AdminRoute><ProductAnalyticsPage /></AdminRoute>
      },
      {
        path: 'admin/analytics/customers',
        element: <AdminRoute><CustomerAnalyticsPage /></AdminRoute>
      },
      {
        path: 'admin/analytics/funnel',
        element: <AdminRoute><FunnelAnalyticsPage /></AdminRoute>
      },
      // Comercial
      {
        path: 'commercial',
        element: <CommercialRoute><CommercialLayout title="Dashboard"><Outlet /></CommercialLayout></CommercialRoute>,
        children: [
          { index: true, element: <CommercialDashboard /> },
          { path: 'customers', element: <CommercialCustomersPage /> },
          { path: 'private-customers', element: <CommercialPrivateCustomersPage /> },
          { path: 'orders', element: <CommercialOrdersPage /> },
          { path: 'orders/:id', element: <OrderDetailPage /> },
        ]
      },
      {
        path: 'distributor',
        element: <CommercialRoute><CommercialLayout title="Dashboard"><Outlet /></CommercialLayout></CommercialRoute>,
        children: [
          { index: true, element: <CommercialDashboard /> },
          { path: 'private-customers', element: <CommercialPrivateCustomersPage /> },
          { path: 'orders', element: <CommercialOrdersPage /> },
          { path: 'orders/:id', element: <OrderDetailPage /> },
        ]
      },
      {
        path: 'quick-order',
        element: <PrivateRoute><QuickOrderPage /></PrivateRoute>
      },
      {
        path: 'ai-disclosure',
        element: <AIDisclosurePage />
      },
      {
        path: '*',
        element: <NotFoundPage />
      }
    ]
  }
]

// Creamos el router para el cliente (solo en el navegador)
export const router = isBrowser
  ? createBrowserRouter(routes)
  : null
